import React, {
  type AriaAttributes,
  type ComponentProps,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import * as WebBrowser from 'expo-web-browser';
import {
  type GestureResponderEvent,
  Linking,
  Platform,
  type Role,
  StyleProp,
  type TextStyle,
} from 'react-native';

import { A } from '@expo/html-elements';

import { usePressableState } from '../../hooks/usePressableState';
import { webOnlyStyles } from '../../utils/webOnlyStyles';
import { BodyText } from '../Text/BodyText';
import { getConditionalStyles } from './TextLink.styles';

import type { TextLinkPalette } from './TextLink.styles';

// ────────────────────────────────────────────────────────────────────────────────

export const TextLink = (props: TextLinkProps) => {
  const {
    testID,
    children,
    href = DEFAULT_HREF,
    newTab,
    disabled,
    active,
    onPress,
    role,
    tabIndex,
    palette = 'primary',
    style: externalStyle,
    underline = true,
    accessibilityLabel,
    accessibilityHint,
    accessibilityHasPopup,
    ...restProps
  } = props;

  const textLinkRef = useRef(null);
  const pressableState = usePressableState(textLinkRef);
  const hasOnPress = !disabled && (href !== undefined || onPress !== undefined);

  // ─────────────────────────────────────────────────────────────────────

  const hrefAttrs = useMemo<ComponentProps<typeof A>['hrefAttrs']>(() => {
    if (!newTab) return;

    return { target: '_blank', rel: 'noopener noreferrer' };
  }, [newTab]);

  // ─── STYLES ──────────────────────────────────────────────────────

  const conditionalStyles = getConditionalStyles({
    pressableState,
    palette,
    disabled,
    active,
    hasOnPress,
  });
  const style = [conditionalStyles, linkWebStyles, externalStyle];
  const linkStyle = disabled && disabledLinkWebStyles;

  // ─── HELPERS ─────────────────────────────────────────────────────

  const openLink = useCallback(async () => {
    if (Platform.OS === 'web') {
      // NOTE: We use RN Linking on Web to open links in a new tab VS popup window.
      await Linking.openURL(href);

      return;
    }

    await WebBrowser.openBrowserAsync(href);
  }, [href]);

  const handleOnPress = useCallback(
    async (event: GestureResponderEvent) => {
      onPress?.(event);

      // NOTE: Web only
      event?.preventDefault();

      if (href && href !== DEFAULT_HREF) {
        await openLink();
      }
    },
    [onPress, href, openLink],
  );

  // ─────────────────────────────────────────────────────────────────

  return (
    <BodyText underline={underline} accessibilityState={{ disabled }} style={style} {...restProps}>
      <A
        testID={testID}
        tabIndex={tabIndex}
        href={Platform.select({ web: href })}
        hrefAttrs={hrefAttrs}
        ref={textLinkRef}
        // @ts-expect-error TS(2322): Type 'false | TextStyle | undefined' is not assign... Remove this comment to see the full error message
        style={linkStyle}
        aria-haspopup={accessibilityHasPopup}
        aria-disabled={disabled}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        onPress={hasOnPress ? handleOnPress : undefined}
        {...(role ? { role } : {})}
      >
        {children}
      </A>
    </BodyText>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const linkWebStyles = webOnlyStyles({
  textUnderlineOffset: 3,
}) as TextStyle;

const disabledLinkWebStyles = webOnlyStyles({
  // @ts-expect-error TS(2322): Type '"not-allowed"' is not assignable to type '(C... Remove this comment to see the full error message
  cursor: 'not-allowed',
}) as TextStyle;

// ─── Constants ───────────────────────────────────────────────────────────────

// NOTE: The default `href` (`#`) is used to force `A` component to use
//       anchor instead of span on the web platform.
const DEFAULT_HREF = '#';

// ─── Types ───────────────────────────────────────────────────────────────────

type TextLinkProps = Readonly<{
  href?: string;
  newTab?: boolean;
  tabIndex?: number;
  palette?: TextLinkPalette;
  style?: StyleProp<TextStyle>;
  disabled?: boolean;
  active?: boolean;
  role?: Role;
  accessibilityHasPopup?: AriaAttributes['aria-haspopup'];
  children: React.ReactNode;
}> &
  Omit<ComponentProps<typeof BodyText>, 'role'>;
